/**
 * Typed wrapper around the REST API.
 *
 * Every call carries an Entra access token. The token is fetched per request from MSAL,
 * which serves it from its cache and silently renews it when it is close to expiring.
 */

import type {
  Calendar,
  Dashboard,
  DayLockState,
  Entry,
  EntryInput,
  Me,
  Role,
  TeamMember,
} from "./types";

const BASE_URL: string = (import.meta.env.VITE_API_BASE_URL ?? "").replace(/\/$/, "");

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }

  /** 423 Locked: the day can no longer be edited. Worth a distinct message in the UI. */
  get isLocked(): boolean {
    return this.status === 423;
  }

  get isForbidden(): boolean {
    return this.status === 403;
  }
}

export type TokenProvider = () => Promise<string>;

async function parseError(response: Response): Promise<string> {
  try {
    const body = await response.json();
    if (typeof body?.detail === "string") return body.detail;
    // FastAPI validation errors arrive as a list of {loc, msg}.
    if (Array.isArray(body?.detail)) {
      return body.detail.map((item: { msg?: string }) => item.msg).filter(Boolean).join("; ");
    }
  } catch {
    // Fall through to the generic message below.
  }
  return `Request failed (${response.status})`;
}

export class ApiClient {
  constructor(private readonly getToken: TokenProvider) {}

  private async request<T>(path: string, init: RequestInit = {}): Promise<T> {
    const token = await this.getToken();
    const response = await fetch(`${BASE_URL}${path}`, {
      ...init,
      headers: {
        ...(init.body ? { "Content-Type": "application/json" } : {}),
        Authorization: `Bearer ${token}`,
        ...init.headers,
      },
    });

    if (!response.ok) {
      throw new ApiError(response.status, await parseError(response));
    }
    if (response.status === 204) {
      return undefined as T;
    }
    return (await response.json()) as T;
  }

  private static scope(userId?: string): string {
    return userId ? `&user_id=${encodeURIComponent(userId)}` : "";
  }

  me(): Promise<Me> {
    return this.request<Me>("/api/me");
  }

  setTheme(theme: string): Promise<Me> {
    return this.request<Me>("/api/me/theme", {
      method: "PATCH",
      body: JSON.stringify({ theme }),
    });
  }

  calendar(month: string, userId?: string): Promise<Calendar> {
    return this.request<Calendar>(`/api/calendar?month=${month}${ApiClient.scope(userId)}`);
  }

  entries(month: string, userId?: string): Promise<Entry[]> {
    return this.request<Entry[]>(`/api/entries?month=${month}${ApiClient.scope(userId)}`);
  }

  createEntry(input: EntryInput, userId?: string): Promise<Entry> {
    const query = userId ? `?user_id=${encodeURIComponent(userId)}` : "";
    return this.request<Entry>(`/api/entries${query}`, {
      method: "POST",
      body: JSON.stringify(input),
    });
  }

  updateEntry(
    id: string,
    patch: Partial<Pick<EntryInput, "task" | "hours" | "minutes" | "note">>,
  ): Promise<Entry> {
    return this.request<Entry>(`/api/entries/${id}`, {
      method: "PATCH",
      body: JSON.stringify(patch),
    });
  }

  deleteEntry(id: string): Promise<void> {
    return this.request<void>(`/api/entries/${id}`, { method: "DELETE" });
  }

  dashboard(month: string, userId?: string): Promise<Dashboard> {
    return this.request<Dashboard>(`/api/dashboard?month=${month}${ApiClient.scope(userId)}`);
  }

  team(month: string): Promise<TeamMember[]> {
    return this.request<TeamMember[]>(`/api/team?month=${month}`);
  }

  addMember(input: { email: string; display_name: string; role: Role }): Promise<TeamMember> {
    return this.request<TeamMember>("/api/team", {
      method: "POST",
      body: JSON.stringify(input),
    });
  }

  updateMember(
    id: string,
    patch: { display_name?: string; role?: Role; is_active?: boolean },
  ): Promise<TeamMember> {
    return this.request<TeamMember>(`/api/team/${id}`, {
      method: "PATCH",
      body: JSON.stringify(patch),
    });
  }

  lockDay(workDate: string, userId: string): Promise<DayLockState> {
    return this.request<DayLockState>(`/api/days/${workDate}/lock?user_id=${userId}`, {
      method: "POST",
    });
  }

  unlockDay(workDate: string, userId: string): Promise<DayLockState> {
    return this.request<DayLockState>(`/api/days/${workDate}/unlock?user_id=${userId}`, {
      method: "POST",
    });
  }

  clearDayOverride(workDate: string, userId: string): Promise<DayLockState> {
    return this.request<DayLockState>(`/api/days/${workDate}/override?user_id=${userId}`, {
      method: "DELETE",
    });
  }

  /** Downloads the month's workbook, honouring the filename the server chose. */
  async exportMonth(month: string): Promise<{ blob: Blob; filename: string }> {
    const token = await this.getToken();
    const response = await fetch(`${BASE_URL}/api/export?month=${month}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!response.ok) {
      throw new ApiError(response.status, await parseError(response));
    }
    const disposition = response.headers.get("Content-Disposition") ?? "";
    const match = /filename="?([^"]+)"?/.exec(disposition);
    return {
      blob: await response.blob(),
      filename: match?.[1] ?? `time-tracking-${month}.xlsx`,
    };
  }
}
